import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {FiBarChart2, FiPlus, FiEdit2, FiTrash2, FiSearch, FiStar, FiGlobe } from 'react-icons/fi';
import { getTourGuides, createTourGuide, updateTourGuide, deleteTourGuide } from '../services/tourGuides';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import TourGuideForm from '../components/TourGuides/TourGuideForm';
import toast from 'react-hot-toast';

const statusColors = {
  available: 'bg-green-100 text-green-800',
  busy: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-gray-100 text-gray-800',
  on_leave: 'bg-red-100 text-red-800',
  pending_verification: 'bg-orange-100 text-orange-800',
};

export default function TourGuides() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGuide, setEditingGuide] = useState(null);
  const [filters, setFilters] = useState({ status: '', language: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchGuides();
  }, [filters]);

  const fetchGuides = async () => {
    setLoading(true);
    try {
      const data = await getTourGuides(filters);
      setGuides(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch tour guides');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (guideData) => {
    try {
      await createTourGuide(guideData);
      toast.success('Tour guide created successfully');
      setShowForm(false);
      fetchGuides();
    } catch (error) {
      toast.error('Failed to create tour guide');
    }
  };

  const handleUpdate = async (id, guideData) => {
    try {
      await updateTourGuide(id, guideData);
      toast.success('Tour guide updated successfully');
      setEditingGuide(null);
      fetchGuides();
    } catch (error) {
      toast.error('Failed to update tour guide');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this tour guide?')) return;
    try {
      await deleteTourGuide(id);
      toast.success('Tour guide deleted successfully');
      fetchGuides();
    } catch (error) {
      toast.error('Failed to delete tour guide');
    }
  };

  const getLanguagesDisplay = (languages) => {
    if (!languages || languages.length === 0) return '-';
    return languages.map(l => l.code.toUpperCase()).join(', ');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Tour Guides</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> Add Tour Guide
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
              <option value="pending_verification">Pending Verification</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm text-gray-600 mb-1">Language</label>
            <select
              value={filters.language}
              onChange={(e) => setFilters({ ...filters, language: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
              <option value="de">German</option>
              <option value="fr">French</option>
              <option value="it">Italian</option>
              <option value="pl">Polish</option>
              <option value="cs">Czech</option>
              <option value="ru">Russian</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Name or phone number"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tour Guides Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Languages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Specialties</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Experience</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                 </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {guides.map((guide) => (
                  <tr key={guide._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-800">{guide.name}</p>
                        {guide.name_arabic && (
                          <p className="text-xs text-gray-500">{guide.name_arabic}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{guide.phone}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {guide.languages?.slice(0, 3).map((lang, idx) => (
                          <span key={idx} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                            {lang.code.toUpperCase()}
                          </span>
                        ))}
                        {guide.languages?.length > 3 && (
                          <span className="text-xs text-gray-500">+{guide.languages.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {guide.specialties?.slice(0, 2).map((spec, idx) => (
                          <span key={idx} className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[guide.status]}`}>
                        {guide.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {guide.rating?.average > 0 ? (
                        <div className="flex items-center text-sm text-yellow-500">
                          <FiStar className="w-3 h-3 fill-current mr-1" />
                          <span>{guide.rating.average}</span>
                          <span className="text-gray-400 ml-1">({guide.rating.count})</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">{guide.experience_years || 0} years</td>
                    <td className="px-6 py-4 text-right">
                      <button
  onClick={() => navigate(`/tour-guides/${guide._id}/stats`)}
  className="text-purple-500 hover:text-purple-700 mr-3"
  title="View Stats"
>
  <FiBarChart2 className="w-4 h-4" />
</button>
                      <button
                        onClick={() => setEditingGuide(guide)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(guide._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {guides.length === 0 && (
            <div className="text-center py-12 text-gray-500">No tour guides found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Tour Guide Form Modal */}
      <TourGuideForm
        isOpen={showForm || !!editingGuide}
        onClose={() => {
          setShowForm(false);
          setEditingGuide(null);
        }}
        onSubmit={editingGuide ? (data) => handleUpdate(editingGuide._id, data) : handleCreate}
        initialData={editingGuide}
      />
    </div>
  );
}
